/* ============================================================
   <predictive-search> — product suggestions with thumbnails
   ============================================================ */

class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.form = this.querySelector('form');
    this.results = this.querySelector('[data-predictive-results]');
    this.list = this.querySelector('[data-predictive-list]');
    this.status = this.querySelector('[data-predictive-status]');
    this.limit = parseInt(this.dataset.limit, 10) || 5;
    this._timer = null;
    this._controller = null;
    this._open = false;

    if (!this.input || !this.results || !this.list) return;

    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-controls', this.list.id || 'PredictiveSearchResults');
    this.input.setAttribute('aria-expanded', 'false');

    this.input.addEventListener('input', () => this._onInput());
    this.input.addEventListener('focus', () => {
      if (this.input.value.trim().length >= 2 && this.list.children.length) this._show();
    });
    this.input.addEventListener('keydown', (e) => this._onKeydown(e));
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this._hide();
    });
  }

  _onInput() {
    clearTimeout(this._timer);
    const q = this.input.value.trim();
    if (q.length < 2) {
      this._hide();
      this.list.innerHTML = '';
      return;
    }
    this._timer = setTimeout(() => this._fetch(q), 180);
  }

  _onKeydown(e) {
    if (e.key === 'Escape') {
      this._hide();
      this.input.blur();
    }
  }

  async _fetch(q) {
    if (this._controller) this._controller.abort();
    this._controller = new AbortController();

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const params = new URLSearchParams({
      q,
      'resources[type]': 'product',
      'resources[limit]': String(this.limit),
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,tag',
    });

    try {
      const res = await fetch(`${root}search/suggest.json?${params}`, {
        signal: this._controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error('suggest failed');
      const data = await res.json();
      const products = (data.resources && data.resources.results && data.resources.results.products) || [];
      this._render(products, q);
    } catch (err) {
      if (err.name === 'AbortError') return;
      this._hide();
    }
  }

  _render(products, q) {
    const usable = products.filter((p) => p && p.url && p.title);
    if (!usable.length) {
      this.list.innerHTML = '';
      this._hide();
      if (this.status) {
        this.status.textContent = this.dataset.emptyLabel || '';
      }
      return;
    }

    this.list.innerHTML = usable.map((p) => this._card(p)).join('');
    if (this.status) {
      this.status.textContent = `${usable.length}`;
    }

    const viewAll = this.querySelector('[data-predictive-all]');
    if (viewAll) {
      const params = new URLSearchParams({ q, type: 'product', 'options[prefix]': 'last' });
      viewAll.href = `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}search?${params}`;
      viewAll.hidden = false;
    }

    this._show();
  }

  _card(p) {
    const imgSrc = this._imageSrc(p);
    const img = imgSrc
      ? `<img class="v3-predictive__thumb" src="${this._escAttr(imgSrc)}" alt="" width="56" height="56" loading="lazy">`
      : `<span class="v3-predictive__thumb v3-predictive__thumb--empty" aria-hidden="true"></span>`;
    const price = this._formatMoney(p.price);
    const vendor = p.vendor
      ? `<span class="v3-predictive__vendor">${this._esc(p.vendor)}</span>`
      : '';

    return `<li role="option">
      <a class="v3-predictive__item" href="${this._escAttr(p.url)}">
        ${img}
        <span class="v3-predictive__meta">
          <span class="v3-predictive__title">${this._esc(p.title)}</span>
          ${vendor}
          <span class="v3-predictive__price">${price}</span>
        </span>
      </a>
    </li>`;
  }

  _imageSrc(p) {
    const raw =
      (typeof p.image === 'string' && p.image) ||
      p.image?.url ||
      p.image?.src ||
      (typeof p.featured_image === 'string' && p.featured_image) ||
      p.featured_image?.url ||
      p.featured_image?.src ||
      null;
    if (!raw) return null;
    const https = String(raw).replace(/^\/\//, 'https://');
    // Prefer a small CDN width when Shopify CDN params are present / appendable
    if (https.includes('cdn.shopify.com') || https.includes('/cdn/shop/')) {
      if (https.includes('width=')) return https.replace(/width=\d+/i, 'width=112');
      return https.includes('?') ? `${https}&width=112` : `${https}?width=112`;
    }
    return https;
  }

  _formatMoney(value) {
    if (value == null || value === '') return '';
    // suggest.json returns decimal strings like "5037.00"
    const num = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (!Number.isFinite(num)) return '';
    if (typeof TB !== 'undefined' && TB.formatMoney) {
      // TB expects cents
      return TB.formatMoney(Math.round(num * 100));
    }
    return num.toLocaleString('sv-SE', { maximumFractionDigits: 0 }) + '\u00a0kr';
  }

  _show() {
    this.results.hidden = false;
    this._open = true;
    this.input.setAttribute('aria-expanded', 'true');
    this.classList.add('is-open');
  }

  _hide() {
    this.results.hidden = true;
    this._open = false;
    this.input.setAttribute('aria-expanded', 'false');
    this.classList.remove('is-open');
  }

  _esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  _escAttr(str) {
    return this._esc(str).replace(/'/g, '&#39;');
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
